import { FirebaseError } from 'firebase/app';
import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  limit,
  serverTimestamp,
  type DocumentData,
  type Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CustomerLog {
  id: string;
  customerId: string;
  message: string;
  createdBy: string;
  userDisplayName: string;
  createdAt: Timestamp;
  updatedAt?: Timestamp;
}

export interface CreateLogData {
  message: string;
  createdBy: string;
  userDisplayName: string;
}

export interface CustomerLogServiceError {
  code: string;
  message: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function logsRef(orgId: string, customerId: string) {
  return collection(db, 'orgs', orgId, 'customers', customerId, 'logs');
}

function docToLog(customerId: string, id: string, data: DocumentData): CustomerLog {
  return {
    id,
    customerId,
    message: data.message as string,
    createdBy: data.createdBy as string,
    userDisplayName: data.userDisplayName as string,
    createdAt: data.createdAt as Timestamp,
    updatedAt: data.updatedAt as Timestamp | undefined,
  };
}

function toServiceError(error: unknown): CustomerLogServiceError {
  if (error instanceof FirebaseError) {
    return { code: error.code, message: error.message };
  }
  return { code: 'unknown', message: 'An unexpected error occurred' };
}

// ─── Service functions ────────────────────────────────────────────────────────

/**
 * Returns the latest N log entries for a customer (default 5), newest first.
 * Used for the activity log preview on the customer detail page.
 */
export async function getRecentLogs(orgId: string, customerId: string, count = 5): Promise<CustomerLog[]> {
  try {
    const q = query(logsRef(orgId, customerId), orderBy('createdAt', 'desc'), limit(count));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => docToLog(customerId, d.id, d.data()));
  } catch (error) {
    throw toServiceError(error);
  }
}

/**
 * Returns all log entries for a customer, newest first.
 * Used for the full logs page (/customers/:id/logs).
 */
export async function getAllLogs(orgId: string, customerId: string): Promise<CustomerLog[]> {
  try {
    const q = query(logsRef(orgId, customerId), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => docToLog(customerId, d.id, d.data()));
  } catch (error) {
    throw toServiceError(error);
  }
}

/**
 * Updates the message of an existing log entry. Only the original author can do this.
 * Sets updatedAt to track that the entry was edited.
 */
export async function updateLog(orgId: string, customerId: string, logId: string, message: string): Promise<void> {
  try {
    if (!message.trim()) {
      throw { code: 'invalid-argument', message: 'Log message cannot be empty.' };
    }
    const ref = doc(db, 'orgs', orgId, 'customers', customerId, 'logs', logId);
    await updateDoc(ref, {
      message: message.trim(),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    if ((error as CustomerLogServiceError).code) throw error;
    throw toServiceError(error);
  }
}

/**
 * Hard-deletes a log entry. Only the original author or an admin can do this.
 */
export async function deleteLog(orgId: string, customerId: string, logId: string): Promise<void> {
  try {
    const ref = doc(db, 'orgs', orgId, 'customers', customerId, 'logs', logId);
    await deleteDoc(ref);
  } catch (error) {
    throw toServiceError(error);
  }
}

/**
 * Appends a new log entry.
 * userDisplayName is stored at write time to avoid repeated auth lookups at render time.
 */
export async function addLog(orgId: string, customerId: string, data: CreateLogData): Promise<CustomerLog> {
  try {
    if (!data.message.trim()) {
      throw { code: 'invalid-argument', message: 'Log message cannot be empty.' };
    }
    const ref = await addDoc(logsRef(orgId, customerId), {
      customerId,
      message: data.message.trim(),
      createdBy: data.createdBy,
      userDisplayName: data.userDisplayName,
      createdAt: serverTimestamp(),
    });
    const snapshot = await getDoc(ref);
    return docToLog(customerId, snapshot.id, snapshot.data()!);
  } catch (error) {
    if ((error as CustomerLogServiceError).code) throw error;
    throw toServiceError(error);
  }
}
